/**
 * V70 Phase C — 참가자 대진표 상세 (Firestore 실시간 구독)
 *
 * - tournamentBracketsPublic/{bracketId} + tournamentBracketMatchesPublic (bracketId) 실시간 구독
 * - 승패 즉시 반영 (서버 push)
 * - 본인 하이라이트: getMyPublicParticipantIds Callable 호출
 * - UID 미노출 (공개 데이터만 사용)
 * - 모바일 가로 스크롤
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { collection, doc, onSnapshot, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db } from '../../../firebase/config';
import { getFunctions } from '../../../firebase/utils';
import { useCurrentUser } from '../../../shared/hooks/useCurrentUser';
import { BracketTreeView } from '../shared/BracketTreeView';
// V70-J — 게임대회 공통 헤더
import { TournamentPageHeader, tournamentPageStyle } from '../shared/TournamentPageHeader';
// V70-M — 게임대회 다크 테마
import { DARK, darkTintBadge, darkSecondaryBtn, darkErrorBox } from '../shared/tournamentDarkTheme';
import {
  PublicBracketView, PublicMatchView, statusColor, statusLabel, tsToMs,
} from '../shared/bracketTypes';

// V70 QA 추가 수정 #4 (2026-10-21) — 참가자 수 단위 (팀 vs 명) 분기.
//   bracket.participants 중 하나라도 mode==='team' 이면 단체전으로 간주.
function isTeamMode(b: PublicBracketView): boolean {
  return (b.participants || []).some((p) => p.mode === 'team');
}

export function BracketDetailPage() {
  const { bracketId } = useParams<{ bracketId: string }>();
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const [bracket, setBracket] = useState<PublicBracketView | null>(null);
  const [matches, setMatches] = useState<PublicMatchView[]>([]);
  const [myIds, setMyIds] = useState<string[]>([]);
  const [notFound, setNotFound] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (!bracketId || !db) return;
    setNotFound(false); setHasError(false);
    // 1) bracket doc 실시간 구독
    const unsubBracket = onSnapshot(
      doc(db, 'tournamentBracketsPublic', bracketId),
      (snap) => {
        if (!snap.exists()) { setNotFound(true); setBracket(null); return; }
        setBracket(snap.data() as PublicBracketView);
      },
      (err) => { console.warn('[BracketDetail] bracket snap err', err); setHasError(true); },
    );
    // 2) matches 실시간 구독
    const q = query(collection(db, 'tournamentBracketMatchesPublic'), where('bracketId', '==', bracketId));
    const unsubMatches = onSnapshot(
      q,
      (snap) => {
        const arr: PublicMatchView[] = snap.docs.map((d) => d.data() as PublicMatchView);
        setMatches(arr);
      },
      (err) => { console.warn('[BracketDetail] matches snap err', err); setHasError(true); },
    );
    return () => { unsubBracket(); unsubMatches(); };
  }, [bracketId]);

  // 본인 하이라이트 조회
  useEffect(() => {
    if (!bracketId || !user) return;
    let cancelled = false;
    (async () => {
      try {
        const fns = getFunctions();
        const r = await httpsCallable<{ bracketId: string }, { publicParticipantIds: string[] }>(fns, 'getMyPublicParticipantIds')({ bracketId });
        if (!cancelled) setMyIds(r.data.publicParticipantIds || []);
      } catch (err) {
        // 참가 안 한 사용자 — noop
        if (!cancelled) setMyIds([]);
      }
    })();
    return () => { cancelled = true; };
  }, [bracketId, user]);

  if (!user) return <div style={{ padding: 24 }}>로그인이 필요합니다.</div>;
  // V70-M — 없음·오류·로딩 상태도 다크 페이지 틀 안에서 표시
  if (notFound) {
    return (
      <div style={pageStyle}>
        <div style={errorBox}>대진표를 찾을 수 없거나 아직 게시되지 않았습니다.</div>
        <button style={backBtn} onClick={() => navigate('/tournament/dashboard')}>← 대시보드</button>
      </div>
    );
  }
  if (hasError) {
    return (
      <div style={pageStyle}>
        <div style={errorBox}>대진표를 불러오지 못했습니다. 잠시 후 새로고침 해 주세요.</div>
        <button style={backBtn} onClick={() => navigate('/tournament/dashboard')}>← 대시보드</button>
      </div>
    );
  }
  if (!bracket) return <div style={{ ...pageStyle, color: DARK.textMuted }}>로딩 중…</div>;

  return (
    <div style={pageStyle}>
      <div style={{ maxWidth: 1400, margin: '0 auto' }}>
        <button style={{ ...backBtn, marginBottom: 10 }} onClick={() => navigate('/tournament/dashboard')}>← 대시보드</button>
        {/* V70-J — e스포츠 공통 헤더. 대진표 이름은 페이지 제목 중복을 피해 본문 소제목(h2)으로 */}
        <TournamentPageHeader page="bracket" style={{ marginBottom: 14 }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <div style={{ minWidth: 0 }}>
            {/* V70-M — 대진표 이름·정보 줄은 다크 배경 위 밝은 색, 상태 배지는 의미 색 유지 + 대비 보정 */}
            <h2 style={{ margin: '0 0 4px', fontSize: 20, fontWeight: 800, overflowWrap: 'anywhere', color: DARK.text }}>{bracket.sourceFormTitle}</h2>
            <div style={{ fontSize: 12, color: DARK.textMuted }}>
              <span style={{ ...badge, ...darkTintBadge(statusColor(bracket.status)) }}>
                {statusLabel(bracket.status)}
              </span>
              {/* V70 QA 추가 수정 #4 (2026-10-21) — 단체전 '참가자 N팀', 개인전 '참가자 N명' */}
              {' · '}참가자 {bracket.participantCount}{isTeamMode(bracket) ? '팀' : '명'}
              {bracket.byeCount > 0 && ` · 부전승 ${bracket.byeCount}건`}
              {' · '}게시 {new Date(tsToMs(bracket.publishedAt) + 9 * 3600 * 1000).toISOString().slice(0, 16).replace('T', ' ')} KST
            </div>
          </div>
          {myIds.length > 0 && (
            <div style={myBadge}>
              👤 내 참가 확인 — 노란색으로 강조됩니다
            </div>
          )}
        </div>

        {bracket.status === 'cancelled' && (() => {
          // V70 QA 리오픈 NNN8 (2026-10-23) — 자동 취소 사유의 내부 marker 를 사용자 화면에서 제거.
          //   예전 데이터에 "재생성을 위한 자동 취소 (admin recreate flow)" 형태로 저장된 cancelReason 이 있을 수 있음.
          //   "(admin recreate flow)" / "(admin ...)" 괄호 marker 를 strip. Firestore 데이터 자체는 변경 없음 (사용자 지시).
          //   일반 사용자 입력 사유 (관리자가 손으로 작성한 자유 텍스트) 는 영향 없음.
          const rawReason = bracket.cancelReason || '';
          const sanitized = rawReason.replace(/\s*\((?:admin\s+[^)]*|자동\s+recreate\s+flow)\)\s*/gi, '').trim();
          return (
            <div style={{ ...errorBox, marginTop: 12 }}>
              대진표가 취소되었습니다.{sanitized ? ` 사유: ${sanitized}` : ''}
            </div>
          );
        })()}

        <div style={{ marginTop: 16 }}>
          <BracketTreeView
            bracket={bracket}
            matches={matches}
            myPublicParticipantIds={myIds}
            showTeamMembers
            theme="dark"
          />
        </div>
      </div>
    </div>
  );
}

// V70-M — 게임대회 다크 테마 (페이지 #22262E, 대진표 칸 #111318)
const pageStyle: React.CSSProperties = tournamentPageStyle({ x: 16, y: 20 });
const backBtn: React.CSSProperties = { ...darkSecondaryBtn, padding: '4px 10px', fontWeight: 500 };
const badge: React.CSSProperties = {
  display: 'inline-block', padding: '2px 10px', borderRadius: 10,
  fontSize: 11, fontWeight: 700,
};
const errorBox: React.CSSProperties = { ...darkErrorBox };
// "노란색으로 강조" 안내 — 대진표 내 참가 강조와 같은 노란 계열
const myBadge: React.CSSProperties = {
  padding: '6px 12px', ...darkTintBadge('#f59e0b'), borderRadius: 6, fontSize: 12, fontWeight: 600,
};

export default BracketDetailPage;
